// src/hooks/useHowto.ts
"use client";

import { useState, useEffect, useMemo } from "react";
import { isOverrideActive, getEffectivePlan } from "@/lib/planMap";

// Firestore の「howto」コレクションのデータ型
interface Howto {
  id: string;
  name: string;
  steps: string[];
}

/**
 * 投稿可否判定
 * - loading : profile 未ロード（新規 or 初期化中）
 * - allowed : 投稿可
 * - denied  : 投稿不可
 */
export type HowtoPostStatus = "loading" | "allowed" | "denied";

export function canPostHowto(profile?: any): HowtoPostStatus {
  if (profile === undefined) return "loading";
  if (!profile) return "denied";

  const toMs = (v: any) =>
    v && typeof v?.toDate === "function" ? v.toDate().getTime() : new Date(v as any).getTime();

  const badge = profile?.badges?.catalyst;
  const badgeActive =
    badge?.level === "facilitator" && badge?.expires_at && toMs(badge.expires_at) > Date.now();

  const eff = (getEffectivePlan(profile) as string) || "";
  /**
   * HowTo 投稿が可能なのは：
   * - Discovery 以上で利用可
   *   （2026-07-22・CEO指示：Discovery Pro限定からDiscovery以上に引き下げ済み。
   *    既に決定済みの内容を実装に反映するだけの対応。サーバー側`/api/howto`の
   *    許可条件は元々discoveryを含んでいたため、これでクライアント側と一致する）
   * - override / catalyst badge による例外
   */
  const planAllowsPost =
   ["discovery", "discovery_pro", "facilitator", "partner"].includes(eff);

   return isOverrideActive(profile) || badgeActive || planAllowsPost
    ? "allowed"
    : "denied";
}

/** Hook 版：UI 側で依存だけを追って再計算 */
export function useHowtoCanPost(profile?: any): HowtoPostStatus {
  return useMemo(() => canPostHowto(profile), [
    profile?.user_plan,
    profile?.previous_plan,
    profile?.next_plan,
    profile?.next_plan_start_date,
    profile?.badges?.catalyst?.level,
    profile?.badges?.catalyst?.expires_at,
    profile?.app?.plan_override,
    profile?.app?.plan_override_expires_at,
  ]);
}

const useHowto = () => {
  const [howtos, setHowtos] = useState<Howto[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchHowtos = async () => {
      try {
        const res = await fetch("/api/howto");
        if (!res.ok) throw new Error("Failed to fetch");
        const fetchedHowtos = await res.json();
        setHowtos(fetchedHowtos);
      } catch (error) {
        console.error("Error fetching howtos:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchHowtos();
  }, []);

  return { howtos, loading };
};

export default useHowto;
